import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Video1Poster from '../../assets/1_Authentic_Innovations.jpg';
import Video1 from '../../assets/1_Authentic_Innovations.mp4';
import Video2Poster from '../../assets/2_User_Centric.jpg';
import Video2 from '../../assets/2_User_Centric.mp4';
import Video3Poster from '../../assets/3_Precision_Performance.jpg';
import Video3 from '../../assets/3_Precision_Performance.mp4';
import Video4Poster from '../../assets/4_Global_Collaboration.jpg';
import Video4 from '../../assets/4_Global_Collaboration.mp4';
import Video5Poster from '../../assets/5_Safety_Integrity.jpg';
import Video5 from '../../assets/5_Safety_Integrity.mp4';
import { FrameLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { H2, Text1 } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import i18n from '../../common/i18n/i18n';
import { fitFontSizeToWidth } from '../../common/utils/TextUtils';
import { USE_VISION_MISSION_SCROLL_PROGRESS_ANIMATION } from '../../config';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import { useOrientation } from '../../hooks/useOrientation';

const PAGE_COUNT = 5;

const pagesData = [
  {
    video: Video1,
    poster: Video1Poster,
    title: 'Authentic\nInnovations',
    subtitle: '비전 & 미션',
    text: `진정성 있는 혁신을 통해 산업 표준이 됩니다. 클린룸/드라이룸 설계, 반도체·2차전지·불소수지코팅기술에 이르기까지, 우리는 지속적인 혁신을 추구합니다.`,
  },
  {
    video: Video2,
    poster: Video2Poster,
    title: 'User\nCentric',
    subtitle: '비전 & 미션',
    text: `사용자 중심의 설계로 최적의 배관 솔루션을 제공합니다. 고객의 요구와 기대를 충족시키기 위해 맞춤형 솔루션을 개발합니다.`,
  },
  {
    video: Video3,
    poster: Video3Poster,
    title: 'Precision\nPerformance',
    subtitle: '비전 & 미션',
    text: `정밀한 성능과 뛰어난 품질로 신뢰성을 보장합니다. 우리는 제품과 서비스의 품질을 지속적으로 개선하여, 최고의 성능을 제공합니다.`,
  },
  {
    video: Video4,
    poster: Video4Poster,
    title: 'Global\nCollaboration',
    subtitle: '비전 & 미션',
    text: `글로벌 협력을 통한 지식 공유와 기술 발전으로 업계를 선도합니다. 세계 각국의 파트너와 협력하여 혁신적인 솔루션을 개발하고, 기술 발전에 기여합니다.`,
  },
  {
    video: Video5,
    poster: Video5Poster,
    title: 'Safety\nIntegrity',
    subtitle: '비전 & 미션',
    text: `모든 이해관계자의 안전과 건강을 최우선으로 하는 안전한 배관 솔루션을 제공합니다. 우리는 고객과 임직원 모두에게 안전한 작업 환경을 보장합니다.`,
  },
];

type IntroduceSection3Props = {
  pageIndex: number;
  progress: number;
  showIndicator?: boolean;
};

const IntroduceSection3 = ({ pageIndex, progress, showIndicator = true }: IntroduceSection3Props) => {
  const { t } = useTranslation();
  const [fadeOutIndex, setFadeOutIndex] = useState<number | null>(null);
  const [visibleIndex, setVisibleIndex] = useState(pageIndex);
  const [zoom, setZoom] = useState(1.0);
  const { isMobile, isPad, screenHeight } = useDeviceType();
  const { horizontal: padding, width: contentWidth } = useResponsivePadding();
  const orientation = useOrientation();
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const areaWidth = (screenWidth - padding * 2);
    if (screenHeight < areaWidth) {
      setZoom(1);
    } else {
      setZoom(areaWidth / screenHeight);
    }
  }, [padding]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const areaWidth = (screenWidth - padding * 2);
      if (screenHeight < areaWidth) {
        setZoom(1);
      } else {
        setZoom(areaWidth / screenHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [padding]);

  useEffect(() => {
    if (pageIndex !== visibleIndex) {
      const timeout = setTimeout(() => {
        setFadeOutIndex(visibleIndex);
        setVisibleIndex(pageIndex);
        setTimeout(() => setFadeOutIndex(null), 600);
      }, 100); // fadeOut 딜레이 100ms 추가
      return () => clearTimeout(timeout);
    }
  }, [pageIndex]);

  const current = pagesData[visibleIndex];
  const fading = fadeOutIndex !== null ? pagesData[fadeOutIndex] : null;
  var tempFontSize = fitFontSizeToWidth((isMobile && isEng) ? 'Through global collaboration, knowledge sharing, and' : '클린룸/드라이룸 설계, 반도체·2차전지·불소수지코팅기술에', contentWidth > 430 ? 430 : contentWidth, { padding: 8 });

  if (screenHeight <= 430) {
    tempFontSize = 14;
  }

  const renderContent = (item: typeof pagesData[0], withTitle: boolean = true) => (
    <>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: `100%`,
          paddingTop: isMobile ? `0px` : `3vh`,
          paddingLeft: padding,
          paddingRight: padding,
        }}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            paddingTop: isMobile && orientation === 'landscape' ? '20px' : '0px'
          }}
        >
          <H2 style={{
            color: 'white',
            margin: 0,
            padding: 0,
            textAlign: 'center',
            display: 'inline-block',
            // fontSize: isMobile ? `4vh` : `5vh`
          }}>{t(current.subtitle)}</H2>
        </div>

        <BTBox height={isMobile ? isPad ? `8vh` : `5vh` : `4vh`} />
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Text1 style={{
            color: 'white',
            whiteSpace: 'pre-line',
            height: (isMobile && isPad) ? `19vh` : `17vh`,
            fontSize: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? tempFontSize :*/ /*isPad ? `14px` :*/ tempFontSize : `2.2vh`,
            lineHeight: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? `${tempFontSize + 4}px` :*/ /*isPad ? `17px` :*/ `${tempFontSize + 5}px` : '3.3vh',
            paddingTop: (isMobile && isPad) ? '0px' : '20px',
          }}><Trans i18nKey={item.text} /></Text1>
        </motion.div>
        <BTBox height={`1vh`} />
        {withTitle && (
          <div
            style={{
              height: isMobile ? (orientation === 'portrait' && !isPad) ? `68px` : `88px` : `20vh`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <motion.div
              initial={{ y: fadeOutIndex == null ? 200 : fadeOutIndex < visibleIndex ? 200 : -200 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <H2 style={{ color: 'white', whiteSpace: 'pre-line'/*, fontSize: isMobile ? `4vh` : `5vh`, lineHeight: isMobile ? `5vh` : `6vh`*/ }}>
                {item.title}
              </H2>
            </motion.div>
          </div>
        )}
        {!withTitle && <div style={{ height: isMobile ? (orientation === 'portrait' && !isPad) ? `68px` : `88px` : `20vh`, overflow: 'hidden', position: 'relative' }} />}
        <video
          src={item.video}
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          poster={item.poster}
          preload="auto"
          disableRemotePlayback
          style={{
            height: isMobile ? `36vh` : `40vh`,
            objectFit: 'cover',
            zIndex: 0,
            marginTop: isMobile ? isPad ? `-10vh` : `-4vh` : `-13vh`,
          }}
        />
      </div>
    </>
  );

  const renderContentWithProgress = (item: typeof pagesData[0], progress: number, withTitle: boolean = true) => (
    <>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: `100%`,
          paddingTop: isMobile ? `0px` : `3vh`,
          paddingLeft: padding,
          paddingRight: padding,
        }}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            paddingTop: isMobile && orientation === 'landscape' ? '20px' : '0px'
          }}
        >
          <H2 style={{
            color: 'white',
            margin: 0,
            padding: 0,
            textAlign: 'center',
            display: 'inline-block',
            // fontSize: isMobile ? `4vh` : `5vh`
          }}>{t(current.subtitle)}</H2>
        </div>

        <BTBox height={isMobile ? isPad ? `8vh` : `5vh` : `4vh`} />
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Text1 style={{
            color: 'white',
            whiteSpace: 'pre-line',
            height: (isMobile && isPad) ? `19vh` : `17vh`,
            fontSize: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? tempFontSize :*/ /*isPad ? `14px` :*/ tempFontSize : `2.2vh`,
            lineHeight: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? `${tempFontSize + 4}px` :*/ /*isPad ? `17px` :*/ `${tempFontSize + 5}px` : '3.4vh',
            paddingTop: (isMobile && isPad) ? '0px' : '20px',
          }}></Text1>
        </motion.div>
        <BTBox height={`1vh`} />
        {withTitle && (
          <div
            style={{
              height: isMobile ? (orientation === 'portrait' && !isPad) ? `68px` : `88px` : `20vh`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <motion.div
              initial={{ y: fadeOutIndex == null ? 200 : fadeOutIndex < visibleIndex ? 200 : -200 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <H2 style={{ color: 'white', whiteSpace: 'pre-line'/*, fontSize: isMobile ? `4vh` : `5vh`, lineHeight: isMobile ? `5vh` : `6vh`*/ }}>
                {item.title}
              </H2>
            </motion.div>
          </div>
        )}
        {!withTitle && <div style={{ height: isMobile ? (orientation === 'portrait' && !isPad) ? `68px` : `88px` : `20vh`, overflow: 'hidden', position: 'relative' }} />}
        <video
          src={item.video}
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          poster={item.poster}
          preload="auto"
          disableRemotePlayback
          style={{
            height: isMobile ? `36vh` : `40vh`,
            objectFit: 'cover',
            zIndex: 0,
            marginTop: isMobile ? isPad ? `-10vh` : `-4vh` : `-13vh`,
          }}
        />
      </div>
    </>
  );

  const renderContentText = (item: typeof pagesData[0], progress: number) => (
    <>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: `100%`,
          paddingTop: isMobile ? `0px` : `3vh`,
          paddingLeft: padding,
          paddingRight: padding,
        }}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            paddingTop: isMobile && orientation === 'landscape' ? '20px' : '0px'
          }}
        >
          <H2 style={{
            color: 'white',
            margin: 0,
            padding: 0,
            textAlign: 'center',
            display: 'inline-block',
            // fontSize: isMobile ? `4vh` : `5vh`
            opacity: 0,
          }}>{t(current.subtitle)}</H2>
        </div>

        <BTBox height={isMobile ? isPad ? `8vh` : `5vh` : `4vh`} />
        <motion.div
          // initial={{ opacity: 0, y: 0 }}
          // animate={{ opacity: 1, y: 0 }}
          // transition={{ duration: 0.4 }}
          style={{
            opacity: progress,
          }}
        >
          <Text1 style={{
            color: 'white',
            whiteSpace: 'pre-line',
            height: (isMobile && isPad) ? `19vh` : `17vh`,
            fontSize: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? tempFontSize :*/ /*isPad ? `14px` :*/ tempFontSize : `2.2vh`,
            lineHeight: isMobile ? /*(orientation === 'portrait' && contentWidth <= 430) ? `${tempFontSize + 4}px` :*/ /*isPad ? `17px` :*/ `${tempFontSize + 5}px` : '3.4vh',
            paddingTop: (isMobile && isPad) ? '0px' : '20px',
          }}><Trans i18nKey={item.text} /></Text1>
        </motion.div>
        <BTBox height={`1vh`} />
        <div style={{ height: isMobile ? (orientation === 'portrait' && !isPad) ? `68px` : `88px` : `20vh`, overflow: 'hidden', position: 'relative' }} />
        <div
          style={{
            height: isMobile ? `36vh` : `40vh`,
            objectFit: 'cover',
            zIndex: 0,
            marginTop: isMobile ? isPad ? `-10vh` : `-4vh` : `-13vh`,
          }}
        />
      </div>
    </>
  );

  const sharedMotionStyle = (zIndex: number): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    color: 'white',
    zIndex,
    pointerEvents: 'none',
  });

  return (
    <VerticalLayout style={{ background: 'black', width: '100%', height: '100%', overflow: 'hidden' }}>
      <FrameLayout style={{ position: 'relative', width: '100%', height: '100%', transform: `scale(${isMobile ? 1.0 : zoom})` }}>
        {USE_VISION_MISSION_SCROLL_PROGRESS_ANIMATION ? (
          <>
            <AnimatePresence>
              {fading && (
                <motion.div
                  key={`fade-out-${fadeOutIndex}`}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  style={sharedMotionStyle(1)}
                >
                  {renderContentWithProgress(fading, progress, false)}
                </motion.div>
              )}

              <motion.div
                key={`fade-in-${visibleIndex}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                style={sharedMotionStyle(2)}
              >
                {renderContentWithProgress(current, progress)}
              </motion.div>
            </AnimatePresence>

            <motion.div
              key={`fade-out-text-${visibleIndex}`}
              style={sharedMotionStyle(2)}
            >
              {renderContentText(pagesData[pageIndex], 1 - progress)}
            </motion.div>

            {pageIndex < 4 && (
              <motion.div
                key={`fade-in-text-${visibleIndex}`}
                style={sharedMotionStyle(1)}
              >
                {renderContentText(pagesData[pageIndex + 1], progress)}
              </motion.div>
            )}
          </>
        ) : (
          <AnimatePresence>
            {fading && (
              <motion.div
                key={`fade-out-${fadeOutIndex}`}
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                style={sharedMotionStyle(1)}
              >
                {renderContent(fading, false)}
              </motion.div>
            )}

            <motion.div
              key={`fade-in-${visibleIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={sharedMotionStyle(2)}
            >
              {renderContent(current)}
            </motion.div>
          </AnimatePresence>
        )}

      </FrameLayout>

      {showIndicator ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? `1vh` : `3vh`,
            height: `0.6vh`,
            width: `calc(100% - ${padding * 2}px)`,
            margin: '10px auto 0',
            zIndex: 1000,
            left: padding,
            right: padding,
            transform: 'translateZ(0)',
            willChange: 'transform, opacity',
          }}
        >
          {Array.from({ length: PAGE_COUNT }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: '100%',
                backgroundColor: i === pageIndex ? '#14B446' : '#D9D9D980',
                transition: 'background-color 0.3s ease',
                borderRadius: '3px',
              }}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? `1vh` : `3vh`,
            height: `0.6vh`,
            width: `calc(100% - ${padding * 2}px)`,
            margin: '10px auto 0',
            zIndex: 1000,
            left: padding,
            right: padding,
            transform: 'translateZ(0)',
            willChange: 'transform, opacity',
          }}
        >
        </div>
      )}

      <BTBox height={isMobile ? isPad ? `6vh` : `4vh` : `8vh`} />
    </VerticalLayout>
  );
};

export default IntroduceSection3;
